import { tiendaDb } from '../lib/supabase';

// ── BANDEJA EMAIL SERVICE ─────────────────────────────────────────────────────

/**
 * Fetch all email ingesta records with optional filters
 */
export const getEmailIngesta = async ({ estado = null, limit = 100, offset = 0 } = {}) => {
  let query = tiendaDb?.from('facturas_email_ingesta')?.select(`
      *,
      proveedor:proveedor_id (id, nombre, email),
      factura:factura_id (id, numero_factura, estado, total)
    `)?.order('received_at', { ascending: false })?.range(offset, offset + limit - 1);

  if (estado && estado !== 'TODAS') {
    query = query?.eq('estado', estado);
  }

  const { data, error } = await query;
  if (error) throw new Error(error.message);
  return data || [];
};

/**
 * Get counters for the bandeja email header
 */
export const getEmailIngestaCounters = async () => {
  const { data, error } = await tiendaDb?.from('facturas_email_ingesta')?.select('estado');

  if (error) throw new Error(error.message);

  const rows = data || [];
  const counts = {
    NUEVA: 0,
    PROCESANDO: 0,
    LISTA_PARA_REVISAR: 0,
    REQUIERE_REVISION: 0,
    CONTABILIZADA: 0,
    DUPLICADA: 0,
    IGNORADA: 0,
    ERROR: 0,
    total: rows?.length,
  };

  rows?.forEach(r => {
    if (counts?.[r?.estado] !== undefined) counts[r.estado]++;
  });

  return counts;
};

/**
 * Get gmail_sync_config (singleton)
 */
export const getGmailSyncConfig = async () => {
  const { data, error } = await tiendaDb?.from('gmail_sync_config')?.select('*')?.limit(1)?.single();

  if (error && error?.code !== 'PGRST116') throw new Error(error.message);
  return data || null;
};

/**
 * Update gmail_sync_config (e.g. import_from_date)
 */
export const updateGmailSyncConfig = async (patch) => {
  const { error } = await tiendaDb?.from('gmail_sync_config')?.update(patch)?.neq('id', '00000000-0000-0000-0000-000000000000'); // update all rows (singleton)

  if (error) throw new Error(error.message);
};

/**
 * Mark an ingesta record as IGNORADA
 */
export const marcarIgnorada = async (ingestaId) => {
  const { error } = await tiendaDb?.from('facturas_email_ingesta')?.update({ estado: 'IGNORADA' })?.eq('id', ingestaId);

  if (error) throw new Error(error.message);
};

/**
 * Trigger gmail-watch-renew Edge Function
 */
export const renewGmailWatch = async () => {
  const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
  const anonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

  const resp = await fetch(`${supabaseUrl}/functions/v1/gmail-watch-renew`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${anonKey}`,
    },
    body: JSON.stringify({}),
  });

  const data = await resp?.json();
  if (!resp?.ok || !data?.ok) {
    throw new Error(data.error || `Watch renewal failed: ${resp.status}`);
  }
  return data;
};

/**
 * Get the public URL for a stored attachment
 */
export const getAttachmentUrl = (storagePath, bucket = 'facturas-compras') => {
  if (!storagePath) return null;
  const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
  return `${supabaseUrl}/storage/v1/object/public/${bucket}/${storagePath}`;
};

/**
 * Get signed URL for private bucket access
 */
export const getAttachmentSignedUrl = async (storagePath, bucket = 'facturas-compras') => {
  if (!storagePath) return null;
  const { data, error } = await tiendaDb?.storage?.from(bucket)?.createSignedUrl(storagePath, 3600); // 1 hour

  if (error) throw new Error(error.message);
  return data?.signedUrl || null;
};

/**
 * Reprocesar an existing ERROR ingesta record using the already-saved PDF from Storage.
 * Does NOT create a new ingesta record. Does NOT treat the same record's hash as a duplicate.
 */
export const reprocesarIngesta = async (ingestaId) => {
  const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
  const anonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

  const resp = await fetch(`${supabaseUrl}/functions/v1/gmail-process-message`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${anonKey}`,
    },
    body: JSON.stringify({ action: 'reprocesar', ingestaId }),
  });

  const data = await resp?.json();
  if (!resp?.ok || !data?.ok) {
    throw new Error(data?.error || `Reprocesar failed: ${resp?.status}`);
  }
  return data;
};

/**
 * Approve a REQUIERE_REVISION invoice → LISTA_PARA_REVISAR
 * Validates: proveedor_id, numero_factura, fecha_factura, total > 0, at least 1 linea
 */
export const aprobarFactura = async (ingestaId, facturaId) => {
  if (!facturaId) throw new Error('No hay factura ERP vinculada para aprobar');

  // Validate factura data
  const { data: fc, error: fcErr } = await tiendaDb?.from('facturas_compra')
    ?.select('proveedor_id, numero_factura, fecha_factura, total')
    ?.eq('id', facturaId)
    ?.single();
  if (fcErr) throw new Error(fcErr?.message);
  if (!fc?.proveedor_id) throw new Error('La factura no tiene proveedor asignado');
  if (!fc?.numero_factura) throw new Error('La factura no tiene número de factura');
  if (!fc?.fecha_factura) throw new Error('La factura no tiene fecha');
  if (!fc?.total || parseFloat(fc?.total) <= 0) throw new Error('El total debe ser mayor que 0');

  // Check at least 1 linea
  const { count } = await tiendaDb?.from('lineas_factura_compra')
    ?.select('id', { count: 'exact', head: true })
    ?.eq('factura_id', facturaId);
  if (!count || count < 1) throw new Error('La factura debe tener al menos 1 línea válida');

  // Update factura estado
  const { error: fcUpErr } = await tiendaDb?.from('facturas_compra')
    ?.update({
      estado: 'LISTA_PARA_REVISAR',
      revisado_manual: true,
      revisado_fecha: new Date()?.toISOString(),
      accion_revision: 'APROBADA',
    })
    ?.eq('id', facturaId);
  if (fcUpErr) throw new Error(fcUpErr?.message);

  // Update ingesta estado
  const { error: ingErr } = await tiendaDb?.from('facturas_email_ingesta')
    ?.update({ estado: 'LISTA_PARA_REVISAR' })
    ?.eq('id', ingestaId);
  if (ingErr) throw new Error(ingErr?.message);
};

/**
 * Delete a factura_compra and its lines; mark ingesta as IGNORADA
 */
export const eliminarFactura = async (ingestaId, facturaId) => {
  // Delete lines first (FK constraint)
  if (facturaId) {
    const { error: linesErr } = await tiendaDb?.from('lineas_factura_compra')
      ?.delete()
      ?.eq('factura_id', facturaId);
    if (linesErr) throw new Error(linesErr?.message);

    // Unlink ingesta from factura before deleting
    await tiendaDb?.from('facturas_email_ingesta')
      ?.update({ factura_id: null })
      ?.eq('id', ingestaId);

    const { error: fcErr } = await tiendaDb?.from('facturas_compra')
      ?.delete()
      ?.eq('id', facturaId);
    if (fcErr) throw new Error(fcErr?.message);
  }

  // Mark ingesta as IGNORADA (not deleted from DB, preserves document)
  const { error: ingErr } = await tiendaDb?.from('facturas_email_ingesta')
    ?.update({ estado: 'IGNORADA' })
    ?.eq('id', ingestaId);
  if (ingErr) throw new Error(ingErr?.message);
};

/**
 * Mark email record as not an invoice (IGNORADA), delete ERP factura if exists
 */
export const marcarNoFactura = async (ingestaId, facturaId) => {
  // Delete lines + factura if they exist
  if (facturaId) {
    await tiendaDb?.from('lineas_factura_compra')?.delete()?.eq('factura_id', facturaId);
    await tiendaDb?.from('facturas_email_ingesta')?.update({ factura_id: null })?.eq('id', ingestaId);
    await tiendaDb?.from('facturas_compra')?.delete()?.eq('id', facturaId);
  }

  // Mark ingesta as IGNORADA, preserve document
  const { error } = await tiendaDb?.from('facturas_email_ingesta')
    ?.update({ estado: 'IGNORADA', motivo_revision: 'NO_ES_FACTURA' })
    ?.eq('id', ingestaId);
  if (error) throw new Error(error?.message);
};

/**
 * Get proveedores list for selector
 */
export const getProveedores = async () => {
  const { data, error } = await tiendaDb?.from('proveedores')
    ?.select('id, nombre, nif_cif, email')
    ?.eq('activo', true)
    ?.order('nombre');
  if (error) throw new Error(error?.message);
  return data || [];
};
